import { isActiveContactOrder } from "../../../../../../../../../../../_shared/_logic/outreach-presentation"
import {
  uniquePropertyCountForWorkOrders,
  validateDemoBatchPlanningPropertyLimit,
} from "../../../../../../../../../_shared/_logic/demo-batch-planning-limit"
import {
  isCancelableAppointment,
  isConfirmableAppointment,
} from "../../../../../../../_shared/_logic/appointment-eligibility"
import type { WorkOrderRow } from "../../../../../../../_shared/_logic/work-order-table-model"
import { selectedWorkOrderRows } from "./batchActions"

export type WorkOrderBatchActionTargets = {
  selected: WorkOrderRow[]
  movable: WorkOrderRow[]
  confirmable: WorkOrderRow[]
  appointmentCreatable: WorkOrderRow[]
  announcementCreatable: WorkOrderRow[]
  announcementCancelable: WorkOrderRow[]
}

export type WorkOrderBatchActionCounts = {
  selected: number
  movable: number
  confirmable: number
  appointmentCreatable: number
  announcementCreatable: number
  announcementCancelable: number
}

export type WorkOrderBatchActionDisabledReasons = {
  complete: string | null
  delete: string | null
  reschedule: string | null
  confirmAppointments: string | null
  createAppointments: string | null
  createAnnouncements: string | null
  cancelAnnouncements: string | null
}

const noValidWorkOrderReason = "Keine markierte Zeile enthält einen gültigen Auftrag."

export const resolveWorkOrderBatchActionTargets = ({
  rows,
  relevantScheduleEventByWorkOrderId,
  activeContactOrderByWorkOrderId,
}: {
  rows: unknown[]
  relevantScheduleEventByWorkOrderId: Record<string, ScheduleEvent>
  activeContactOrderByWorkOrderId: Record<string, ContactOrder>
}): WorkOrderBatchActionTargets => {
  const selected = selectedWorkOrderRows(rows)
  const hasAppointment = (row: WorkOrderRow) =>
    Boolean(relevantScheduleEventByWorkOrderId[row.id]?.id)
  const hasMovableAppointment = (row: WorkOrderRow) =>
    isCancelableAppointment(relevantScheduleEventByWorkOrderId[row.id])
  const hasConfirmableAppointment = (row: WorkOrderRow) =>
    isConfirmableAppointment(relevantScheduleEventByWorkOrderId[row.id])
  const hasMissingAnnouncementPlan = (row: WorkOrderRow) =>
    hasAppointment(row) && row.actionRequiredOverview?.reason === "announcement_plan_missing"
  const hasActiveContactOrder = (row: WorkOrderRow) =>
    Boolean(
      activeContactOrderByWorkOrderId[row.id]?.id &&
      isActiveContactOrder(activeContactOrderByWorkOrderId[row.id]),
    )

  return {
    selected,
    movable: selected.filter(hasMovableAppointment),
    confirmable: selected.filter(hasConfirmableAppointment),
    appointmentCreatable: selected.filter((row) => !hasAppointment(row)),
    announcementCreatable: selected.filter(hasMissingAnnouncementPlan),
    announcementCancelable: selected.filter(hasActiveContactOrder),
  }
}

export const resolveWorkOrderBatchActionCounts = (
  targets: WorkOrderBatchActionTargets,
): WorkOrderBatchActionCounts => ({
  selected: targets.selected.length,
  movable: targets.movable.length,
  confirmable: targets.confirmable.length,
  appointmentCreatable: targets.appointmentCreatable.length,
  announcementCreatable: targets.announcementCreatable.length,
  announcementCancelable: targets.announcementCancelable.length,
})

export const resolveWorkOrderBatchActionDisabledReasons = (
  counts: WorkOrderBatchActionCounts,
  options: {
    targets?: WorkOrderBatchActionTargets | null
    trialRestrictionsActive?: boolean | null
  } = {},
): WorkOrderBatchActionDisabledReasons => {
  const limitReason = (rows: WorkOrderRow[]) =>
    validateDemoBatchPlanningPropertyLimit(
      uniquePropertyCountForWorkOrders(rows.map((row) => row.workOrder)),
      {
        trialRestrictionsActive: options.trialRestrictionsActive,
      },
    )

  return {
    complete: counts.selected ? null : noValidWorkOrderReason,
    delete: counts.selected ? null : noValidWorkOrderReason,
    reschedule: counts.movable
      ? limitReason(options.targets?.movable ?? [])
      : "Keine markierte Zeile hat einen verschiebbaren Termin.",
    confirmAppointments: counts.confirmable
      ? limitReason(options.targets?.confirmable ?? [])
      : "Keine markierte Zeile hat einen Termin, der noch bestätigt werden kann.",
    createAppointments: counts.appointmentCreatable
      ? limitReason(options.targets?.appointmentCreatable ?? [])
      : counts.selected
        ? "Alle markierten Aufträge haben bereits einen Termin."
        : noValidWorkOrderReason,
    createAnnouncements: counts.announcementCreatable
      ? limitReason(options.targets?.announcementCreatable ?? [])
      : counts.selected
        ? "Keine markierte Zeile hat einen Termin ohne Ankündigungsplan."
        : noValidWorkOrderReason,
    cancelAnnouncements: counts.announcementCancelable
      ? null
      : "Keine markierte Zeile hat einen aktiven Ankündigungsplan.",
  }
}

export const formatBatchActionCount = (count: number): string =>
  new Intl.NumberFormat("de-DE").format(count)
